import { concat, evolve, filter, isEmpty, keys, mergeRight, not, pipe, prop, propEq, reject, sortBy } from "ramda";
import { get as apiGet, post } from "src/services/api";
import { SYSTEM_MODES } from "./createAppSlice";
import {
  filterByRelation,
  getMainKgHashFromVersionFile,
  makeGraphTriplets
} from "../../pages/KnowledgeExplorer/knowledgeUtils";
import { botPortalService, kgviewerService, storageService } from "../../services";
import { DRAFT_STATUS, objectToTriplets, PROVIDER, relationIn, Relations } from "../../utils/helpers/triplets";
import { CONFIGURATIONS_DIRECTORY_NAME, CACHE_DURATION } from "@consts";
import { queryClient } from "src/lib/react-query/queryClient";
import { FETCH_QUERY_KEYS } from "src/queries/consts";
import { fetchDraft, fetchAgentKnowledge } from "src/queries/services/assistants";
import { CONFIGURATION_VERSIONS } from "src/utils/core/consts";
const createTripletsSlice = (set, get) => ({
  // original triplets = most up to date master triplets: used for comparison while you are editing a draft
  originalTriplets: [],

  currentTriplets: [],
  displayedTriplets: [],
  knowledgeGraphAsTriplets: [],
  tripletsStatus: DRAFT_STATUS.IDLE,
  failedToCreateDraft: false,
  failedToPublishDraft: false,
  failedToUpdateDraft: false,
  infoPopovers: {
    draftExpiration: false,
    buildDraft: false,
    setLive: false,
    preparePreview: false
  },
  couldNotLoadTriplets: false,

  clearTriplets: () => {
    set({
      originalTriplets: [],
      currentTriplets: [],
      displayedTriplets: [],
      draft: null,
      tripletsStatus: DRAFT_STATUS.IDLE
    });
  },

  initTriplets: async () => {
    const { selectedAccountId, selectedAssistant } = get();
    set({ tripletsStatus: DRAFT_STATUS.LOADING });

    try {
      const originalTriplets = await botPortalService.originalTriplets();
      set({ originalTriplets });
    } catch (err) {
      set({ couldNotLoadTriplets: true });
    }

    const draft = await queryClient.fetchQuery({
      queryKey: [FETCH_QUERY_KEYS.DRAFT, selectedAccountId, selectedAssistant.assistantId],
      queryFn: async () => await fetchDraft(selectedAccountId, selectedAssistant.assistantId),
      staleTime: CACHE_DURATION.ONE_MINUTE,
      enabled: !!selectedAccountId && !!selectedAssistant?.assistantId
    });

    const { originalTriplets, systemMode, getValidationResults } = get();
    let draftTriplets = [];

    if (draft) {
      set({ draft });

      if (systemMode === SYSTEM_MODES.EDIT) {
        getValidationResults();
      }
      draftTriplets = (await botPortalService.tripletsFromDraft({ draft })) || [];
    }
    const currentTriplets = draftTriplets;

    set({
      currentTriplets,
      displayedTriplets: systemMode === SYSTEM_MODES.EDIT && draft ? draftTriplets : originalTriplets,
      draft,
      tripletsStatus: DRAFT_STATUS.IDLE
    });
  },

  getKnowledgeGraph: async () => {
    const { draft } = get();
    if (draft?.isFirstDraft || !draft) return;
    const knowledgeData = await fetchAgentKnowledge(draft.assistantId);
    const versionsFileObj = knowledgeData.draft;
    // non deployed assistants do not have official versions yet
    if (Object.keys(versionsFileObj).length === 0) return;

    const graphHash = getMainKgHashFromVersionFile(versionsFileObj);
    const mainKnowledgeGraph = await kgviewerService.getGraph({
      graphHash
    });
    const knowledgeGraphAsTriplets = makeGraphTriplets(mainKnowledgeGraph);
    set({ knowledgeGraphAsTriplets });
  },

  updateCurrentTriplets: async (updateFn, type) => {
    const {
      userData,
      draft,
      displayedTriplets,
      pageName,
      infoPopovers,
      selectedAssistant,
      setBuildDraftResponse
    } = get();
    // we need to sort current triplets so we can check differentiation between current and original triplets
    const updatedTriplets = sortBy(prop(0))(updateFn(displayedTriplets));
    set({ displayedTriplets: updatedTriplets, currentTriplets: updatedTriplets });

    const tripletsDir = `${CONFIGURATIONS_DIRECTORY_NAME}/${selectedAssistant?.assistantFolder}`;
    const commitMessage = `Edit ${tripletsDir}/triplets.csv`;

    set({ tripletsStatus: DRAFT_STATUS.SAVING });
    const actionName = type ? `${type} triplets` : "";

    if (!draft) {
      try {
        const newDraft = await botPortalService.createDraftAndUpdateTriplets({
          fileContent: updatedTriplets,
          commitMessage,
          userData
        }, { pageName, actionName });
        set({ infoPopovers: { ...infoPopovers, draftExpiration: true }, draft: newDraft });
      } catch (error) {
        console.error("Error creating a new draft: ", error);

        if (error.response.status === 409) {
          set({ failedToCreateDraft: true, draft: null });
        }
      }
    } else {
      try {
        await botPortalService.editTriplets({
          draft,
          fileContent: updatedTriplets,
          commitMessage,
          userData
        }, { pageName, actionName });
        const branchDeployerResponse = await apiGet({ endpoint: `/assistants/${selectedAssistant.assistantId}/build/status?branchName=${draft?.branchName}` });
        setBuildDraftResponse(branchDeployerResponse);
      } catch (error) {
        console.error("Error editing an existing draft: ", error);
        set({ failedToUpdateDraft: true });
      }
    }
    queryClient.refetchQueries({ queryKey: [FETCH_QUERY_KEYS.DRAFT] });
    set({
      tripletsStatus: DRAFT_STATUS.IDLE
    });
  },

  publishCurrentTripletsToDraft: async (changeDescription) => {
    const { draft, pageName, currentChannelSettings } = get();
    set({ tripletsStatus: DRAFT_STATUS.PUBLISHING });
    try {
      const endpoint = `/assistants/${draft?.assistantId}/deploy/live`;
      await post({
        endpoint,
        data: {
          description: changeDescription
        },
        headers: { pageName }
      });
      queryClient.refetchQueries({ queryKey: [FETCH_QUERY_KEYS.DRAFT] });

      set({
        draft: {},
        initialChannelSettings: currentChannelSettings,
        draftChannelSettings: null
      });
    } catch (error) {
      set({ failedToPublishDraft: true });
    }

    set({
      tripletsStatus: DRAFT_STATUS.IDLE
    });
  },

  deletePublishedDraft: async (draftToDelete) => {
    const {
      userData,
      originalTriplets,
      collections,
      originalCollectionConfig,
      collectionSpec,
      pageName,
      closeSpeechPlayer,
      policiesData
    } = get();

    try {
      await botPortalService.deleteDraft({
        draft: draftToDelete,
        userData
      }, { actionName: "Delete published draft", pageName });
      queryClient.refetchQueries({ queryKey: [FETCH_QUERY_KEYS.DRAFT] });
    } catch (error) {
      console.error("Error deleting draft: ", error);
      return { error: true };
    }

    queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.CHANNEL_SETTINGS] });
    closeSpeechPlayer();

    set({
      draft: null,
      currentTriplets: [],
      displayedTriplets: originalTriplets,
      selectedCollection: { ...originalCollectionConfig, spec: collectionSpec },
      collections: {
        ...collections,
        configRef: collections?.originalConfig,
        currentConfig: collections?.originalConfig
      },
      validationResults: { live: {}, draft: {}, current: {} },
      shouldRecreatePreview: false,
      policiesData: {
        ...policiesData,
        policies: {
          ...policiesData.policies,
          current: policiesData.policies.live
        }
      }
    });
  },

  deleteCurrentDraft: async () => {
    const {
      draft,
      originalTriplets,
      userData,
      collections,
      originalCollectionConfig,
      collectionSpec,
      pageName,
      setBuildDraftResponse,
      policiesData,
      closeSpeechPlayer
    } = get();

    if (!draft?.isFirstDraft) {
      set({ isDiscardingChanges: true });

      try {
        await botPortalService.deleteDraft({
          draft,
          userData
        }, { actionName: "Delete current draft", pageName });
        queryClient.refetchQueries({ queryKey: [FETCH_QUERY_KEYS.DRAFT] });
      } catch (error) {
        console.error("Error deleting draft: ", error);
        set({ isDiscardingChanges: false });
        return { error: true };
      }

      queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.CHANNEL_SETTINGS] });
      closeSpeechPlayer();

      set({
        selectedCollection: { ...originalCollectionConfig, spec: collectionSpec },
        currentTriplets: [],
        displayedTriplets: originalTriplets,
        collections: {
          ...collections,
          configRef: collections?.originalConfig,
          currentConfig: collections?.originalConfig
        },
        draft: null,
        validationResults: { live: {}, draft: {}, current: {} },
        isDiscardingChanges: false,
        shouldRecreatePreview: false,
        policiesData: {
          ...policiesData,
          policies: {
            ...policiesData?.policies,
            current: policiesData?.policies?.live,
            draft: [],
            local: policiesData?.policies?.live
          },
          selectedPolicy: {
            ...policiesData?.selectedPolicy,
            draft: null,
            current: policiesData?.selectedPolicy?.live,
            local: policiesData?.selectedPolicy?.live
          },
          spec: {
            live: policiesData?.spec?.live,
            draft: [],
            current: policiesData?.spec?.live
          }
        }
      });
      setBuildDraftResponse({ status: "", url: "" });
    }
  },

  updateResultCardConfig: async (modifiedConfig) => {
    const result_card_triplet_format_fns = {
      [Relations.RENDER_TITLE]: subtitle =>
        subtitle ? `name,${subtitle}` : "name"
    };
    const relationsToReject = keys(modifiedConfig);
    const tripletsToConcat = pipe(
      evolve(result_card_triplet_format_fns),
      filter(pipe(isEmpty, not)),
      mergeRight({ id: PROVIDER }),
      objectToTriplets
    )(modifiedConfig);

    const { updateCurrentTriplets } = get();
    const tripletsUpdateFn = pipe(
      reject(relationIn(relationsToReject)),
      concat(tripletsToConcat)
    );
    await updateCurrentTriplets(tripletsUpdateFn);
  },

  isTriggerInGraph: (node) => {
    const { knowledgeGraphAsTriplets, originalTriplets } = get();
    const isInGraph = filterByRelation(Relations.CONCEPT_TRIGGER)(
      knowledgeGraphAsTriplets
    ).some(trigger => trigger[2] === node);
    const isInTripletsTriggers = filterByRelation(Relations.CONCEPT_TRIGGER)(
      originalTriplets
    ).some(trigger => trigger[2] === node);

    return isInGraph && !isInTripletsTriggers
      ? ["This trigger already exists in the knowledge graph"]
      : [];
  },

  discardDraftIfNoChanges: async (updateCollectionConfig) => {
    const {
      originalTriplets,
      displayedTriplets,
      deleteCurrentDraft,
      originalCollectionConfig,
      currentCollectionConfig,
      draft,
      collections,
      initialChannelSettings,
      draftChannelSettings
    } = get();
    const updatedConfig = updateCollectionConfig || currentCollectionConfig;
    const currentCollectionsConfig = collections?.currentConfig?.map(({ status, ...rest }) => rest);
    if (JSON.stringify(displayedTriplets) === JSON.stringify(originalTriplets)
      && JSON.stringify(updatedConfig) === JSON.stringify(originalCollectionConfig)
      && JSON.stringify(currentCollectionsConfig) === JSON.stringify(collections.originalConfig)
      && JSON.stringify(initialChannelSettings) === JSON.stringify(draftChannelSettings)
    ) {
      if (draft) {
        const { error } = await deleteCurrentDraft() || {};
        if (error) return false;
      }
      return true;
    }
    return false;
  },

  setFailedToCreateDraftSelector: (failedToCreateDraft) => {
    set({ failedToCreateDraft });
  },

  setFailedToPublishDraftSelector: (failedToPublishDraft) => {
    set({ failedToPublishDraft });
  },

  setInfoPopovers: (type, data) => {
    const { infoPopovers } = get();
    set({ infoPopovers: { ...infoPopovers, [type]: data } });
  },

  setCouldNotLoadTriplets: (couldNotLoadTriplets) => {
    set({ couldNotLoadTriplets });
  }
});

export default createTripletsSlice;
